// CSV export helper — used by the finance tables (PO register, levy by-object,
// vendor analysis, PO close-worklist, board packet) to export the data the page
// already has in memory, respecting the current filters. Two layers:
//
//   • toCsv(rows, columns?)  — PURE string builder (RFC-4180 quoting). Unit-tested
//       in the node test env; no DOM. Exported so callers and tests share one
//       escaping implementation.
//   • exportCsv(filename, rows, columns?) — browser wrapper: builds the CSV via
//       toCsv() and triggers a download via a Blob + object URL. No-op outside a
//       browser (guards on `document`), so importing it never breaks SSR/tests.
//
// "rows" is an array of plain objects. Headers come from `columns` when given
// (so we control order + human labels); otherwise from the union of row keys.

export interface CsvColumn<T> {
  /** object key to read from each row */
  key: keyof T & string;
  /** human-readable header (defaults to the key) */
  header?: string;
  /** optional value formatter (e.g. round numbers, strip quotes) */
  format?: (value: unknown, row: T) => unknown;
}

/**
 * RFC-4180 field escaping: wrap in double-quotes when the value contains a
 * comma, double-quote, CR or LF, doubling any embedded double-quotes. `null` /
 * `undefined` render as empty. Numbers render with full precision (no thousands
 * separators) so the CSV stays machine-parseable; format upstream if you want
 * rounding.
 */
export function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return "";
  let s: string;
  if (typeof value === "number") {
    s = Number.isFinite(value) ? String(value) : "";
  } else if (typeof value === "boolean") {
    s = value ? "true" : "false";
  } else {
    s = String(value);
  }
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build a CSV string from an array of row objects.
 *
 * @param rows    plain objects (one per data row)
 * @param columns optional explicit column spec (order + headers + formatters).
 *                When omitted, columns are inferred from the union of row keys
 *                in first-seen order, with the raw key as the header.
 */
export function toCsv<T extends Record<string, unknown>>(
  rows: readonly T[],
  columns?: readonly CsvColumn<T>[],
): string {
  const cols: CsvColumn<T>[] = columns ? [...columns] : inferColumns(rows);
  const headerLine = cols
    .map((c) => escapeCsvField(c.header ?? c.key))
    .join(",");
  const bodyLines = rows.map((row) =>
    cols
      .map((c) => {
        const raw = row[c.key];
        const val = c.format ? c.format(raw, row) : raw;
        return escapeCsvField(val);
      })
      .join(","),
  );
  // CRLF line endings per RFC-4180 (Excel-friendly); trailing newline.
  return [headerLine, ...bodyLines].join("\r\n") + "\r\n";
}

function inferColumns<T extends Record<string, unknown>>(
  rows: readonly T[],
): CsvColumn<T>[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  return keys.map((k) => ({ key: k as keyof T & string }));
}

/** Ensure a `.csv` extension and strip characters awkward in filenames. */
export function normalizeCsvFilename(name: string): string {
  const base = name.replace(/[\\/:*?"<>|]+/g, "-").trim() || "export";
  return /\.csv$/i.test(base) ? base : `${base}.csv`;
}

/**
 * Build a CSV from `rows` and trigger a browser download named `filename`.
 * Reusable across every finance table. No-op (returns the CSV string) when no
 * DOM is present, so it's safe to import anywhere.
 */
export function exportCsv<T extends Record<string, unknown>>(
  filename: string,
  rows: readonly T[],
  columns?: readonly CsvColumn<T>[],
): string {
  const csv = toCsv(rows, columns);
  if (typeof document === "undefined" || typeof URL === "undefined") {
    return csv; // SSR / test env — nothing to download.
  }
  // Prepend a UTF-8 BOM so Excel reads non-ASCII (e.g. é, ↺) correctly.
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = normalizeCsvFilename(filename);
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return csv;
}
